import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, Trash2, Hash } from 'lucide-react';
import { ChestNumberRange, ChestNumberRangesApi } from '@/services/chest-number-ranges.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input } from '@/components/ui/Input';
import { toast } from '@/stores/toast.store';

const rangeSchema = z
  .object({
    gradeCategory: z.string().min(1, 'Required'),
    rangeStart: z.coerce.number().int().min(0),
    rangeEnd: z.coerce.number().int().min(0),
  })
  .refine((v) => v.rangeEnd >= v.rangeStart, { message: 'Range end must be >= range start', path: ['rangeEnd'] });
type FormValues = z.infer<typeof rangeSchema>;

/**
 * Org-admin setup: reserve a chest-number range per grade, shared/pooled
 * across every school in the org — so two schools' candidates for the
 * same grade can never collide on the same chest number at the physical
 * competition. New feature, no legacy equivalent. A grade with no
 * configured range here keeps using the old per-school counter.
 */
export function ChestNumberRangesPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<{ open: boolean; range?: ChestNumberRange }>({ open: false });
  const [confirm, setConfirm] = useState<{ open: boolean; range?: ChestNumberRange }>({ open: false });

  const { data: ranges = [], isLoading } = useQuery({
    queryKey: ['chest-number-ranges'],
    queryFn: () => ChestNumberRangesApi.list(),
  });

  const upsert = useMutation({
    mutationFn: (vals: { id?: string; payload: FormValues }) =>
      vals.id
        ? ChestNumberRangesApi.update(vals.id, { rangeStart: vals.payload.rangeStart, rangeEnd: vals.payload.rangeEnd })
        : ChestNumberRangesApi.create(vals.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chest-number-ranges'] });
      setModal({ open: false });
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Could not save range'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => ChestNumberRangesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chest-number-ranges'] });
      setConfirm({ open: false });
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Could not delete range'),
  });

  return (
    <>
      <PageHeader
        title="Chest Number Ranges"
        description="Reserve a chest-number range per grade, shared across every school in your organization — so no two schools' candidates can collide on the same number at the competition. A grade with no range set here falls back to the old per-school numbering."
        actions={
          <button className="btn-primary" onClick={() => setModal({ open: true })}>
            <Plus className="mr-1.5 h-4 w-4" /> New range
          </button>
        }
      />

      <DataTable<ChestNumberRange>
        rows={ranges}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No chest-number ranges configured yet — every grade is still using the old per-school counter."
        columns={[
          {
            key: 'no',
            header: 'No.',
            width: '1%',
            render: (_r, index) => <span className="text-slate-400">{index + 1}</span>,
          },
          {
            key: 'gradeCategory',
            header: 'Grade',
            render: (r) => (
              <span className="flex items-center gap-1.5 font-medium text-slate-900">
                <Hash className="h-3.5 w-3.5 text-slate-400" /> {r.gradeCategory}
              </span>
            ),
          },
          {
            key: 'range',
            header: 'Range',
            render: (r) => (
              <span className="text-slate-700">
                {r.rangeStart} – {r.rangeEnd}
              </span>
            ),
          },
          {
            key: 'nextNumber',
            header: 'Next available',
            render: (r) =>
              r.nextNumber > r.rangeEnd ? (
                <Badge tone="red">Exhausted</Badge>
              ) : (
                <span className="text-slate-600">{r.nextNumber}</span>
              ),
          },
          {
            key: 'remaining',
            header: 'Remaining',
            render: (r) => <span className="text-slate-500">{Math.max(0, r.rangeEnd - r.nextNumber + 1)}</span>,
          },
        ]}
        actions={(r) => (
          <>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
              onClick={() => setModal({ open: true, range: r })}
              title="Widen/adjust range"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => setConfirm({ open: true, range: r })}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
      />

      <RangeFormModal
        open={modal.open}
        range={modal.range}
        onClose={() => setModal({ open: false })}
        onSubmit={(vals) => upsert.mutate({ id: modal.range?.id, payload: vals })}
        saving={upsert.isPending}
        errorMsg={errMsg(upsert.error)}
      />

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.range && remove.mutate(confirm.range.id)}
        loading={remove.isPending}
        title="Delete this range?"
        message={`Future registrations for "${confirm.range?.gradeCategory}" will fall back to the old per-school counter. Already-assigned chest numbers are not affected.`}
        confirmText="Delete range"
      />
    </>
  );
}

function RangeFormModal({
  open,
  range,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  range?: ChestNumberRange;
  onClose: () => void;
  onSubmit: (v: FormValues) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(rangeSchema),
    values: {
      gradeCategory: range?.gradeCategory ?? '',
      rangeStart: range?.rangeStart ?? ('' as any),
      rangeEnd: range?.rangeEnd ?? ('' as any),
    },
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={range ? `Edit "${range.gradeCategory}" range` : 'New chest-number range'}
      description="Shared across every school in your organization."
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(onSubmit)} disabled={saving}>
            {saving ? 'Saving…' : range ? 'Save changes' : 'Create range'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Grade" required className="sm:col-span-2" error={errors.gradeCategory?.message} hint={range ? 'Cannot be changed after creation' : 'e.g. Grade 3 — must match the program item grade exactly'}>
          <Input {...register('gradeCategory')} placeholder="Grade 3" disabled={!!range} />
        </Field>
        <Field label="Range start" required error={errors.rangeStart?.message}>
          <Input type="number" min={0} {...register('rangeStart')} placeholder="100" />
        </Field>
        <Field label="Range end" required error={errors.rangeEnd?.message}>
          <Input type="number" min={0} {...register('rangeEnd')} placeholder="399" />
        </Field>

        {errorMsg && <div className="sm:col-span-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </form>
    </Modal>
  );
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
